import React, { useContext, useEffect } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router';
import {
  Button,
  CondensedMedium,
  Form,
  Gutter,
  Input,
  UnauthorizedError,
  CondensedLarge,
  Well,
  Grid,
  GridRow,
  GridColumn,
  SmallHeader,
  AxiomLogo,
  PageContext,
} from '@axiom/ui';
import { SchemaEmail } from '@axiom/types';

import sideImageUrl from '../../public/svg/head-bubbles.svg?url';
import { DefaultLayout } from '../../layouts/DefaultLayout/DefaultLayout';
import { WindowUtil } from '../../utils/WindowUtil';
import { MfaUtil } from '../../utils/mfa-util';
import { AuthApi } from '../../api/auth-api';
import { AuthResetsApi } from '../../api/auth-resets-api';
import { LoginContext } from '../LoginContext/LoginContext';

import { PasswordFormSchema } from './password-form-schema';

export type PasswordProps = {
  name?: string;
};

export const Password = ({ name = 'PASSWORD' }: PasswordProps) => {
  const { setTitle } = useContext(PageContext);
  const navigate = useNavigate();
  const location = useLocation();
  const { providedEmail } = useParams();
  const search = new URLSearchParams(location.search);
  const { setLoginState } = useContext(LoginContext);

  useEffect(() => {
    setTitle('Axiom Law - Login');
  }, [setTitle]);
  useEffect(() => {
    if (!SchemaEmail.safeParse(providedEmail).success) {
      void navigate(
        WindowUtil.getUrlWithRelayState(`/`, {
          location,
        })
      );
    }
  }, [location, providedEmail]);

  const handleForgotPasswordClick = async (
    event: React.MouseEvent<HTMLAnchorElement>
  ) => {
    event.preventDefault();

    const body = {
      email: providedEmail as string,
      RelayState: search.get('RelayState') || undefined,
    };
    await AuthResetsApi.createForgotPassword(body);

    void navigate(`../forgot-password`);
  };

  return (
    <DefaultLayout sideImageUrl={sideImageUrl} name={name}>
      <Grid centered>
        <GridRow>
          <GridColumn largeScreenWidth={9} tabletWidth={9} mobileWidth={12}>
            <Gutter bottom="24px" className="text-align-center">
              <AxiomLogo width="230px" />
            </Gutter>
            <Gutter bottom="24px">
              <SmallHeader>Sign In</SmallHeader>
            </Gutter>
          </GridColumn>
        </GridRow>
        <GridRow>
          <GridColumn largeScreenWidth={9} tabletWidth={9} mobileWidth={12}>
            <Form
              name="PASSWORD_FORM"
              onSubmit={async (_changedData, actions, values) => {
                try {
                  const { data: jwt } = await AuthApi.createAuth({
                    password: values.password,
                    email: providedEmail,
                    mfaCode: null,
                  });

                  setLoginState({
                    email: providedEmail,
                    password: values.password,
                  });

                  MfaUtil.handleNavigation(navigate, location, jwt);
                } catch (e) {
                  if (!(e instanceof UnauthorizedError)) {
                    throw e;
                  }
                  actions.setFieldError(
                    'password',
                    'Invalid email/password. Please try again.'
                  );
                }
              }}
              schema={PasswordFormSchema}
              initialValues={{ password: '' }}
            >
              {({ fireSubmit }) => {
                return (
                  <>
                    <Gutter bottom="8px">
                      <Well>
                        <CondensedLarge name="email">
                          {providedEmail}
                        </CondensedLarge>
                      </Well>
                    </Gutter>
                    <Gutter bottom="24px">
                      <CondensedMedium>
                        <Link
                          to={WindowUtil.getUrlWithRelayState(`/`, {
                            location,
                          })}
                          data-test="NOT_YOU_LINK"
                        >
                          Not you? Sign in with a different account.
                        </Link>
                      </CondensedMedium>
                    </Gutter>
                    <Gutter bottom="8px">
                      <Input label="Password" name="password" />
                    </Gutter>
                    <Gutter bottom="16px">
                      <a
                        href="#"
                        onClick={e => {
                          void handleForgotPasswordClick(e);
                        }}
                        data-test="FORGOT_PASSWORD"
                      >
                        Forgot password?
                      </a>
                    </Gutter>
                    <Button fluid onClick={fireSubmit} name="sign-in">
                      Sign in
                    </Button>
                  </>
                );
              }}
            </Form>
          </GridColumn>
        </GridRow>
      </Grid>
    </DefaultLayout>
  );
};
